import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Form, Formik } from "formik";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { createChecklistFromHseDocument } from "../../../../api/hse/documents";

export default function NewChecklistFromHseDialog({
  open,
  onClose,
  hseDocumentId,
  defaultTitle,
  onCreated,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) setCreating(false);
  }, [open]);

  const handleSubmit = async (values) => {
    if (!hseDocumentId) return;

    setCreating(true);
    try {
      const res = await createChecklistFromHseDocument(hseDocumentId, {
        title: values.title || defaultTitle,
        customer_id: values.customer_id || undefined,
        project_id: values.project_id || undefined,
      });

      if (res?.errors) {
        toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("quality.checklist.type.hse.create_failed"));
        return;
      }

      const checklistId = res?.id;
      if (!checklistId) {
        toast.error(t("quality.checklist.type.hse.create_failed"));
        return;
      }

      toast.success(res?.message || t("quality.checklist.type.hse.checklist_created"));
      onClose?.();
      onCreated?.();
      navigate(`/dashboard/hse-ks/checklists/${checklistId}`);
    } catch (err) {
      const message =
        err?.response?.data?.errors?.join?.(", ") ||
        err?.response?.data?.error ||
        t("quality.checklist.type.hse.create_failed");
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("quality.checklist.type.hse.create_checklist")}
      size="md"
    >
      <Formik
        enableReinitialize
        initialValues={{
          title: defaultTitle || "",
          customer_id: null,
          project_id: null,
        }}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form className="space-y-4 px-1 py-2">
            <PMField
              name="title"
              type="text"
              label={t("Title")}
              placeholder={t("Enter checklist title")}
              {...formik}
            />

            <CustomerSelector selected={formik.values.customer_id} {...formik} />

            <ProjectSelector selected={formik.values.project_id} {...formik} />

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={creating}
                className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {creating ? t("Creating...") : t("quality.checklist.type.hse.create_checklist")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}
